/**
 * /docs — the public documentation index (mockup carbon/04 frame, carbon/01
 * type). The breadcrumb, then the 01 brief: an action-first h1, one lede, the
 * one primary action and a tertiary beside it. The two guides follow as
 * condensed-grid tiles that are links.
 */
import { Link } from 'react-router-dom'
import type { ActionBrief } from '@/lib/brief-shape'
import { BriefHero, ButtonLink, CondensedGrid, IconArrowRight, Tile, TileHeader } from '@/components/carbon'
import { PUBLIC_LINK } from '@/components/PublicLayout'
import { DocsPage, DocSection } from './docs-ui'

const sections = [
  {
    title: 'Onboarding guide',
    description: 'Step-by-step instructions to set up Guardian monitoring for your Microsoft 365 tenant.',
    href: '/docs/onboarding',
    cta: 'Read the onboarding guide',
  },
  {
    title: 'Permissions & security',
    description: 'What permissions Guardian requires, which service principal holds each one, and how your data is protected.',
    href: '/docs/permissions',
    cta: 'Review the permissions',
  },
]

const DOCS_BRIEF: ActionBrief = {
  headline: 'Start with the onboarding guide.',
  lede: '**Two guides** cover connecting your Microsoft 365 tenant to Guardian and what Guardian reads once it is connected.',
  primaryAction: { label: 'Read the onboarding guide', href: '/docs/onboarding' },
}

export default function DocsIndex() {
  return (
    <DocsPage crumbs={[{ label: 'Documentation' }]}>
      <BriefHero
        brief={DOCS_BRIEF}
        size="pillar"
        className="mt-8"
        secondaryAction={
          <ButtonLink to="/docs/permissions" kind="tertiary" icon={<IconArrowRight />}>
            Review the permissions
          </ButtonLink>
        }
      />

      <CondensedGrid as="ul" columns={{ base: 1, 'c-md': 2 }} className="mt-12 max-w-[64rem]">
        {sections.map((section) => (
          <li key={section.title} className="flex min-w-0">
            <Tile to={section.href} padding={24} className="flex w-full flex-col">
              <TileHeader title={section.title} />
              <p className="m-0 mt-3 text-body text-text-secondary">{section.description}</p>
              <span className="mt-6 inline-flex items-center gap-2 text-body text-link">
                {section.cta}
                <IconArrowRight />
              </span>
            </Tile>
          </li>
        ))}
      </CondensedGrid>

      <DocSection title="Questions about setup or security?">
        <p className="m-0">
          The Guardian team answers setup and security questions by email at{' '}
          <a href="mailto:support@iuvotech.com" className={PUBLIC_LINK}>
            support@iuvotech.com
          </a>
          . Already set up?{' '}
          <Link to="/dashboard" className={PUBLIC_LINK}>
            Sign in to Guardian
          </Link>
          .
        </p>
      </DocSection>
    </DocsPage>
  )
}
